<template>
  <div :class="{ 'rounded-t-sm shadow-sm': mobile }">
    <div class="text-center" :class="{ 'mx-5  mt-5 flex': mobile }">
      <v-avatar size="112" class="my-2 self-center">
        <v-img :src="getAvatar(assistant)" lazy-src="/img/profile.png"> </v-img>
        <template #placeholder>
          <v-row fill-height align-center ma-0>
            <v-progress-circular
              indeterminate
              color="grey-lighten-5"
            ></v-progress-circular>
          </v-row>
        </template>
      </v-avatar>
      <h3 v-if="mobile" class="ml-3 break-all">
        {{ assistant.display_name }}
      </h3>
    </div>
    <div
      v-if="
        !sessionUserHasPermission('virtualassistant') &&
        hasAdminPermissionInCheckout &&
        !checkoutStore.matchingCriteria.opportunityCheckout
      "
      class="grid grid-flow-row"
    >
      <vg-btn
        :color="vgOcean"
        large
        class="mx-7 my-4 px-3 lg:mx-3"
        @click="addCandidate"
      >
        <span> {{ $t('portal:add.candidate.button').toUpperCase() }} </span>
      </vg-btn>
    </div>
  </div>
</template>

<script>
import tailwind from 'tailwind.config';
import {
  getUser,
  sessionUserHasPermission,
  hasSession,
} from '@/services/clientSessionService';
import { API_URL } from '@/config';
import { mapStores, storeToRefs } from 'pinia';
import { useCheckoutStore } from '@/stores/checkout';
import { useClientDashboardStore } from '@/stores/clientDashboard';
import { useUserStore } from '@/stores/user';
import { getState } from '@/services/matchingService';
import { saveProject } from '@/services/projectService';

export default {
  name: 'CurrentAvailability',
  props: {
    assistant: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    mobile: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
  },
  data() {
    const { user } = storeToRefs(useUserStore());
    const { initClientProjects } = useClientDashboardStore();

    return {
      user,
      initClientProjects,
      API_URL,
      vgOcean: tailwind.theme.extend.colors.vgocean[500],
    };
  },
  computed: {
    ...mapStores(useCheckoutStore),
    currentAvailability() {
      return this.assistant.available_hours_per_month -
        this.assistant.monthly_hours_committed >
        0
        ? this.assistant.available_hours_per_month -
            this.assistant.monthly_hours_committed
        : 0;
    },
    criteriaNotMet() {
      return !(
        ['client', 'admin'].some((permission) =>
          this.user?.permissions.includes(permission)
        ) &&
        this.checkoutStore.matchingCriteria?.rids &&
        this.checkoutStore.matchingCriteria?.hours &&
        this.checkoutStore.matchingCriteria?.hours?.length ===
          this.checkoutStore.matchingCriteria?.rids?.length
      );
    },
    hasAdminPermissionInCheckout() {
      return !!(
        this.user?.permissions.includes('admin') &&
        this.checkoutStore.matchingCriteria?.uuid &&
        this.$router.currentRoute.value.meta?.loadInitialCheckout
      );
    },
  },
  async created() {
    if (!this.checkoutStore.matchingCriteria?.uuid && getState()?.query?.uuid) {
      await this.checkoutStore.loadProjectByUUID(getState().query.uuid);
    } else if (getState()?.query) {
      //update matching criteria using local storage
      await this.checkoutStore.updateMatchingCriteria(getState().query);
    }
  },
  methods: {
    sessionUserHasPermission,
    getUser,
    getAvatar(assistant) {
      return `${API_URL}/file/avatar/${assistant ? assistant.avatar_id : -1}`;
    },

    async addCandidate() {
      if (
        !this.checkoutStore.matchingCriteria?.focusedRid &&
        !this.checkoutStore.matchingCriteria?.rids &&
        !this.checkoutStore.matchingCriteria?.hours
      ) {
        // adding candidate without going through checkout
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          rids: this.assistant.role_ids,
          hours: new Array(this.assistant.role_ids.length).fill(10),
        });
      }

      const ridsToCheck =
        this.checkoutStore.matchingCriteria.focusedRid &&
        this.checkoutStore.matchingCriteria.focusedRid.length !== 0
          ? this.checkoutStore.matchingCriteria.focusedRid
          : this.checkoutStore.matchingCriteria.rids;
      const ridsToKeep =
        ridsToCheck.filter(
          (rid) =>
            !(this.checkoutStore.matchingCriteria?.cart || []).some(
              (va) =>
                va.assistant_id === this.$route.params.id && va.role_id === rid
            )
        ) || [];

      if (ridsToKeep.length === 0) {
        if (!hasSession()) {
          return this.$router.push('/client/signup/');
        } else {
          this.$root.$snackbar.message(
            this.$t('portal:currentAvailability.add.candidate.message')
          );

          return this.$router.push(`/search?step=candidates`);
        }
      }

      if (!this.checkoutStore.matchingCriteria.confirmationShown) {
        await this.$root.$potentialMatchDialog.open();
        await this.checkoutStore.updateMatchingCriteria({
          ...this.checkoutStore.matchingCriteria,
          confirmationShown: true,
        });
      } else {
        this.$root.$potentialMatchDialog.open(true);
      }

      const ridsAssistantHave = ridsToKeep.filter((rid) =>
        this.assistant.role_ids.includes(rid)
      );

      let newCart = [
        ...(this.checkoutStore.matchingCriteria.cart || []),
        ...ridsToKeep.map((role_id) => {
          return {
            hours: this.checkoutStore.matchingCriteria.hours[
              this.checkoutStore.matchingCriteria.rids.indexOf(role_id)
            ],
            role_id,
            assistant_id: ridsAssistantHave.includes(role_id)
              ? this.$route.params.id
              : this.checkoutStore.specialVGUser.uuid,
            avatar_id: ridsAssistantHave.includes(role_id)
              ? this.assistant.avatar_id
              : this.checkoutStore.specialVGUser.avatar_id,
            known_as: ridsAssistantHave.includes(role_id)
              ? this.assistant.display_name
              : this.checkoutStore.specialVGUser.display_name,
            tids: this.checkoutStore.matchingCriteria.tids,
            sids: this.checkoutStore.matchingCriteria.sids,
            timezone: {
              timezone: this.checkoutStore.matchingCriteria.timezone,
              offset_range: this.checkoutStore.matchingCriteria.offset_range,
            },
            self_identifications: this.checkoutStore.matchingCriteria
              .self_identifications,
          };
        }),
      ];

      //filter out -1 candidate for ridsToKeep
      newCart = newCart.filter(
        (item) =>
          !ridsToKeep.find(
            (rid) => item.role_id === rid && item.assistant_id === -1
          )
      );

      await this.checkoutStore.updateMatchingCriteria({
        ...this.checkoutStore.matchingCriteria,
        cart: newCart,
      });
      if (this.criteriaNotMet) {
        if (!hasSession()) {
          return this.$router.push('/client/signup/');
        } else if (this.sessionUserHasPermission('virtualassistant')) {
          this.$root.$snackbar.error(
            this.$t(
              'portal:currentAvailability.add.candidate.permission.error"'
            )
          );
        } else {
          this.$root.$snackbar.error(
            this.$t('portal:currentAvailability.add.candidate.project.error"')
          );
        }
      }

      if (sessionUserHasPermission('client')) {
        if (!this.checkoutStore.matchingCriteria.uuid) {
          const projectUUID = await saveProject().then(
            (response) => response.project_uuid
          );

          await this.checkoutStore.updateMatchingCriteria({
            ...this.checkoutStore.matchingCriteria,
            uuid: projectUUID,
          });

          await this.initClientProjects({ forceRefresh: true });
        }
      }
      this.$router.push(`/search?step=candidates`);
    },
  },
};
</script>
